import { gql } from "@apollo/client";
import { EpocusDeviceSecretPurpose } from "elephant-web/gql";
import { useCallback, useEffect, useMemo } from "react";
import { useInterval } from "usehooks-ts";
import { useEpocusDeviceSecretQuery } from "./__generated__/device-secret-source.gql";

export type DeviceSecretSource = {
  value?: string;
  loading: boolean;
  error?: Error;
};

const RETRY_INTERVAL_MS = 15 * 1000;

export const useDeviceSecretSource = (): DeviceSecretSource => {
  const { data, loading, error, refetch } = useEpocusDeviceSecretQuery({
    variables: { purpose: EpocusDeviceSecretPurpose.RxdbEncryption },
    fetchPolicy: "network-only",
  });

  const value = data?.epocusDeviceSecret.value;

  const missingValueError = useMemo(
    () => new Error("epocusDeviceSecret returned no value"),
    [],
  );
  const sourceError =
    error ?? (!loading && !value ? missingValueError : undefined);

  const retry = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  useEffect(() => {
    if (!sourceError) return;
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, [sourceError, retry]);

  useInterval(retry, sourceError ? RETRY_INTERVAL_MS : null);

  if (sourceError) {
    return { loading: false, error: sourceError };
  }

  return { value, loading };
};

gql`
  query EpocusDeviceSecret($purpose: EpocusDeviceSecretPurpose!) {
    epocusDeviceSecret(purpose: $purpose) {
      value
    }
  }
`;
